import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Boxes,
  CalendarRange,
  ClipboardList,
  Plus,
  Target,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  PageHeader,
  Spinner,
  Tabs,
  type BadgeTone,
} from '../../ui';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { useSprints } from '../../features/sprintboard/useSprints';
import { sprintStatusLabel } from '../../features/sprintboard/models/sprintboardModel';
import type { Sprint, SprintStatus } from '../../features/sprintboard/models/types';
import { CreateSprintModal } from './CreateSprintModal';
import { PrePlanningSection } from './PrePlanningSection';

const STATUS_TONE: Record<SprintStatus, BadgeTone> = {
  planning: 'info',
  active: 'success',
  complete: 'neutral',
};

type SprintsTab = 'sprints' | 'preplanning';

/** Formats an ISO date (yyyy-mm-dd) for display; passes through when unset. */
function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Sprints — the room's sprints as a simple card list, newest first, each
 * showing its status, dates, and goal, with a "Plan sprint" action that opens
 * the two-pane planning surface. "Create Sprint" opens the create modal.
 *
 * The page also hosts a month-wise "Pre-Planning" tab (sprint-wise pre-plan
 * Markdown docs grouped by month, each with a link to its canonical planning
 * doc). Pre-Planning is tenant-scoped and works with or without an active room.
 */
export function SprintsPage() {
  const navigate = useNavigate();
  const { activeRoom, activeRoomId } = useActiveRoom();
  const { sprints, loading, error, create } = useSprints();
  const [createOpen, setCreateOpen] = useState(false);
  const [tab, setTab] = useState<SprintsTab>('sprints');

  const openPlan = (sprint: Sprint) =>
    navigate(`/work/sprints/${sprint.id}/plan`);

  const tabs = (
    <Tabs
      className="mb-5"
      value={tab}
      onChange={(id) => setTab(id as SprintsTab)}
      items={[
        {
          id: 'sprints',
          label: 'Sprints',
          icon: <CalendarRange className="h-4 w-4" aria-hidden="true" />,
        },
        {
          id: 'preplanning',
          label: 'Pre-Planning',
          icon: <ClipboardList className="h-4 w-4" aria-hidden="true" />,
        },
      ]}
    />
  );

  /** The Sprints tab body — the existing sprint list (room-scoped). */
  const sprintsBody = () => {
    if (!activeRoomId) {
      return (
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="No active room"
          description="Sprints live inside a room. Select or create a room to start planning."
          action={
            <Button
              leftIcon={<Boxes className="h-4 w-4" />}
              onClick={() => navigate('/rooms')}
            >
              Go to Rooms
            </Button>
          }
        />
      );
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading sprints" />
        </div>
      );
    }

    if (error) {
      return (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load sprints"
          description={error}
        />
      );
    }

    if (sprints.length === 0) {
      return (
        <EmptyState
          icon={<CalendarRange className="h-6 w-6" />}
          title="No sprints yet"
          description="Create the first sprint for this room, then plan work into it from the backlog."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
              Create Sprint
            </Button>
          }
        />
      );
    }

    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {sprints.map((s) => (
          <Card key={s.id} interactive onClick={() => openPlan(s)}>
            <CardBody className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-zinc-100">{s.name}</h3>
                <Badge tone={STATUS_TONE[s.status]}>
                  {sprintStatusLabel(s.status)}
                </Badge>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-zinc-500">
                <CalendarRange className="h-3.5 w-3.5" aria-hidden="true" />
                {fmtDate(s.startDate)} — {fmtDate(s.endDate)}
              </p>
              {s.goal && (
                <p className="flex items-start gap-1.5 text-xs text-zinc-400">
                  <Target className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="line-clamp-2">{s.goal}</span>
                </p>
              )}
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPlan(s);
                  }}
                >
                  Plan sprint
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        icon={<CalendarRange className="h-5 w-5" />}
        title="Sprints"
        description={
          <span className="inline-flex items-center gap-1.5">
            Sprints in
            <Badge tone="accent">{activeRoom?.name ?? 'active room'}</Badge>
          </span>
        }
        actions={
          tab === 'sprints' && activeRoomId ? (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
              Create Sprint
            </Button>
          ) : undefined
        }
      />

      {tabs}

      {tab === 'preplanning' ? <PrePlanningSection /> : sprintsBody()}

      <CreateSprintModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={create}
        onCreated={(sprint) => navigate(`/work/sprints/${sprint.id}/plan`)}
      />
    </div>
  );
}

export default SprintsPage;
